import { StaticImage } from "gatsby-plugin-image"

import { blogUrl } from "../../services/blogLinks"

const idcData = {
  color: "#d2c3ff",
  heroTitle: `How IDC used photos, posters and avatars to drive social sharing`,
  heroImage: (
    <StaticImage
      src="../../images/case-study/idc/hero.png"
      alt="idc-technology-research-conference-photo-ai-hero"
    />
  ),
  metrics: {
    quote: `
      Table of Contents<br/><br/>
      <ul style="list-style-type: disc; padding: 0 60px; text-align: left;">
        <li style="margin-bottom: 16px;"><a href='#section-0'>IDC’s challenge: Making events more engaging and shareable</a></li>
        <li style="margin-bottom: 16px;"><a href='#section-1'>The strategy: Riding the new wave of event engagement</a></li>
        <li style="margin-bottom: 16px;"><a href='#section-2'>Features used</a></li>
        <li class='mb-2'><a href='#section-3'>The impact: More events, more engagement, more ROI</a></li>
        <li class='mb-2'><a href='#section-4'>The result: From skepticism to full adoption</a></li>
        <li class='mb-2'><a href='#section-5'>Why IDC chose Premagic over Ingo</a></li>
      </ul>`,
  },
  images: [
    {
      img: (
        <StaticImage
          src="../../images/case-study/idc/banner.jpeg"
          alt="idc-technology-research-conference-photo-ai-primary-banner"
        />
      ),
    },
    {
      img: (
        <StaticImage
          src="../../images/case-study/idc/banner2.png"
          alt="idc-technology-research-conference-photo-ai-secondary-banner"
        />
      ),
    },
  ],
  gallery: (
    <StaticImage
      src="../../images/case-study/idc/gallery.png"
      alt="idc-technology-research-conference-photo-ai-banner"
    />
  ),
  caseStudyData: [
    {
      title: "IDC’s Challenge: Making events more engaging & shareable",
      info1: `<b>IDC (International Data Corporation)</b> is a global powerhouse in market intelligence, advisory services, and events. Every year, they host multiple high-profile events across APAC, India, the Middle East, and South Africa, bringing together some of the brightest minds in the industry. But despite the scale and success of their events, IDC wanted to take engagement to the next level.<br/><br/>Their challenge was to create an interactive and memorable experience that attendees wouldn’t just enjoy but actively share. IDC was looking for a solution that could:<br/><br/>
      <ul style="list-style-type: disc; padding-left: 20px">
      <li class='mb-2'>Add an element of fun and excitement to their events.</li>
      <li class='mb-2'>Encourage onsite promotion and social sharing among attendees.</li>
      <li class='mb-2'>Provide seamless content distribution across multiple regions.</li>
      <li class='mb-2'>Overcome initial skepticism about AI-powered event technology.</li>
      </ul><br/><br/>Previously, IDC had been using <b>Ingo</b> for attendee engagement but felt they needed a more tailored and impactful approach. That’s where Premagic came in.`,
      testimonial: {
        img: (
          <StaticImage
            src="../../images/case-study/idc/avatar.jpeg"
            alt="idc-technology-research-conference-photo-ai-testimonial"
          />
        ),
        message: `“IDC came in with a clear goal. They wanted engagement that felt organic, not forced. Once they saw how Premagic could blend creativity with automation, the hesitation disappeared.“`,
        name: "Bhakti Kapasi",
        info: `Account Executive,<br/> Premagic`,
      },
      inlineLink: {
        text: "BREAKING STEREOTYPES AT THE BANKNOTE CONFERENCE",
        link: "https://premagic.com/case-study/currency-research",
      },
    },
    {
      title: "The Strategy: Riding the new wave of event engagement",
      info1: `IDC partnered with Premagic to revolutionize their event experience, integrating a suite of AI-driven solutions that transformed how attendees interacted with their brand.`,
    },
    {
      title: "Features Used:",
      info1: `<ul style="list-style-type: disc; padding-left: 20px">
      <li class='mb-2'><a href='https://premagic.com/smart-photo-distribution-for-seamless-events/'>AI-powered Photo Distribution</a></li>
      <li class='mb-2'><a href='https://premagic.com/create-event-marketing-posters/'>Event Marketing Posters</a></li>
      <li class='mb-2'><a href='https://premagic.com/avatar-studio/'>AI-powered Avatars</a></li>

      </ul>`,

      info2: `The first breakthrough came with the introduction of <b>AI-powered avatars a-feature Premagic had been developing and finally launched at IDC</b>. The idea was simple: attendees could create AI-enhanced versions of themselves as superheroes, executives, or fantasy characters. It added a light, personal touch to the event and gave people something unexpected to share. But it didn’t stop there.<br/><br/>These avatars were printed instantly onsite, turning them into a tangible keepsake that attendees could take home. A fun, shareable activation that attendees could post on social media while boosting IDC’s online presence organically.<br/><br/>The second game-changer was <a href='${blogUrl("photoDistribution")}'>AI-powered photo distribution</a>. In previous events, attendees had to manually search for their photos or rely on delayed distribution. With Premagic, AI automatically tagged and delivered event photos to attendees in real time. This seamless process removed all friction, making it effortless for attendees to find and share their moments while also amplifying IDC’s brand visibility across social platforms.<br/><br/>Finally, IDC leveraged <a href='${blogUrl("advocacyPosters")}'>branded event posters</a> to drive pre-event engagement. Instead of standard announcement posters, IDC rolled out Premagic’s <b>professionally designed I-am-attending posters</b> months in advance, giving attendees visually compelling content to share. By launching these assets well in advance, atleast 2-3 months before each event, IDC managed to build ample anticipation and created a buzz well before doors opened.`,

      gridViewTitle: "Key Features That Made It Work",
      inlineLink: {
        text: "RETHINKING I-AM-ATTENDING POSTERS FOR SAASBOOMI ANNUAL 2025",
        link: blogUrl("rethinkingAttendingPosters"),
      },
    },
    {
      title: "The Impact: More events, more engagement, more ROI",
      info1: `The results spoke for themselves. At one of their most recent events, <b>IDC Middle East CIO Summit 2025</b>, Premagic’s AI-powered tools helped turn a room full of attendees into a marketing engine.<br/><br/>The advocacy posters generated <b>over 1,074 referral links from just over 212 participants</b>, which in turn drove <b>1,133 unique clicks</b>, resulting in a return on investment of <b>7,245%</b> from the campaign alone.<br/><br/>The avatar feature, introduced for the first time at this event, was quickly embraced. Nearly <b>1,000 custom avatars were created by 286 attendees</b>, indicating high per-user engagement and shareability.<br/><br/>On the visual content front, Premagic’s AI-powered photo distribution once again delivered results. <b>6,147+ professional photos were automatically matched and sent to attendees</b> in real time, who received their images directly on WhatsApp or email, generating a potential <b>ROI of $17,698</b>. These photos made their way across social channels organically, reinforcing IDC’s brand presence and helping extend the event’s life beyond the venue.`,
      inlineLink: {
        text: "BREAKING LANGUAGE BARRIERS AT GLA'S GLOBAL LOGISTICS CONFERENCE",
        link: "https://premagic.com/case-study/gla-global",
      },
      image: (
        <StaticImage
          src="../../images/case-study/idc/banner2.png"
          alt="idc-technology-research-conference-photo-ai"
        />
      ),
    },
    {
      title: "The Result: From skepticism to full adoption",
      info1: `The number of events Premagic powered for IDC grew significantly - from 10 to 12 last year to over 20 this year. In response, IDC shifted from a per-event pricing model to an annual partnership, integrating advocacy and photo distribution across their event portfolio in APAC, India, the Middle East, and South Africa.<br/><br/>Perhaps the most telling sign of success is that <b>IDC made the full switch from Ingo to Premagic</b>, recognizing that the AI-driven activations provided a level of engagement that traditional solutions simply couldn’t match.`,
    },
    {
      title: "Why IDC chose Premagic over Ingo",
      info1: `Initially, IDC was skeptical about AI-powered event solutions. But after seeing the real-world impact, it became clear that Premagic’s approach was a game-changer. Here’s why IDC made the switch:<br/><br/>
      <ul style="list-style-type: disc; padding-left: 20px">
      <li style="margin-bottom: 16px;">Ingo lacked interactive elements like AI-powered avatars that made events fun and engaging.</li>
      <li style="margin-bottom: 16px;">There were no onsite activations, such as printed avatars that created real-time excitement.</li>
      <li style="margin-bottom: 16px;">Limited branding opportunities meant missed chances for automated, shareable poster campaigns.</li>
      <li class='mb-2'>Premagic delivered a fully customized, AI-driven experience tailored to IDC’s exact needs.</li>
      </ul><br/><br/>IDC’s journey with Premagic is proof that the right event tech can transform passive attendees into active brand advocates. From fun, interactive activations to seamless photo distribution and automated branding, IDC turned their events into high-engagement experiences that attendees loved and talked about.<br/><br/>Looking to elevate your event experience like IDC? <a>Let’s talk.</a>`,
    },
  ],
  isCTAActive: true,
}

export default idcData
